/* eslint-disable react-hooks/exhaustive-deps */
import { Address, DigitalProduct, OrderType, UserInfo } from 'types';
import {
  AddressValidationInput,
  AddressValidationOutput,
  GetDigitalProductsForCustomerOrderDocument,
  GetUserDataDocument,
  MagentoOrdersDocument,
  UserData_Set_Input,
  UserData,
  useRegisterAndLoginUserMutation,
  useUpdateUserDataMutation,
  ValidateAddressDocument,
  HubSpotBlogDocument,
} from 'graphql/generated/userApi';
import { useCartContext } from 'CartContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { UseMutationState, useClient, OperationContext } from 'urql';
import jwt_decode from 'jwt-decode';

const GROUPING_SERVICE_URL = process.env.REACT_APP_GROUPING_SERVICE_URL;

export interface UseGetUserState {
  error?: Error;
  fetching: boolean;
  loggedInUserId?: string;
  userInfo?: UserInfo;
  setLoggedInUserId: (userId?: string) => void;
  setUserInfo: (user?: UserInfo) => void;
}

export function useGetUserInfo(): UseGetUserState {
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(
    getUserInfoFromStorage(),
  );
  const [loggedInUserId, setLoggedInUserId] = useState<string | undefined>(
    getUserInfoFromStorage()?.id,
  );
  const [error, setError] = useState<Error | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);

  const client = useClient();
  const { token } = useToken();

  const setUserInfoWrapper = useCallback((newUserInfo?: UserInfo) => {
    setUserInfo(newUserInfo);
    setUserInfoStorage(newUserInfo);
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }
    const id = userIdFromToken(token);
    setLoggedInUserId(id);
    setFetching(true);

    client
      .query(GetUserDataDocument, { id }, userContext(token))
      .toPromise()
      .then(result => {
        if (result.data?.userData_by_pk) {
          const resultUserInfo = userDataToUserInfo(result.data.userData_by_pk);
          if (resultUserInfo.email === userInfo?.email) {
            setUserInfoWrapper(combineUserInfos(resultUserInfo, userInfo));
          } else {
            setUserInfoWrapper(resultUserInfo);
          }
        }
        setFetching(false);
      })
      .catch(queryError => {
        setError(queryError);
        setFetching(false);
      });
  }, [token]);

  useEffect(() => {
    if (loggedInUserId && userInfo && !userInfo.id) {
      setUserInfoWrapper({
        id: loggedInUserId,
        ...userInfo,
      });
    }
  }, [userInfo, loggedInUserId]);

  return {
    error,
    fetching,
    loggedInUserId,
    userInfo,
    setLoggedInUserId,
    setUserInfo: setUserInfoWrapper,
  };
}

export function useUpdateUser(): [UseMutationState, (user: UserInfo) => void] {
  const { token } = useToken();

  const [state, updateUserData] = useUpdateUserDataMutation();

  const updateUser = (user: UserInfo) => {
    const userData = userInfoToUserData(user);
    updateUserData({ id: user.id, userData }, userContext(token));
  };

  return [state, updateUser];
}

export function useRegisterAndLogin(): [
  UseMutationState,
  (user: UserInfo, password: string) => void,
] {
  const [state, executeMutation] = useRegisterAndLoginUserMutation();

  const registerAndLogin = (user: UserInfo, password: string) => {
    const userData = userInfoToUserData(user);
    const userRoleRequestsByName = [
      { exchangeName: 'Ellipsis', roleName: 'member' },
    ];
    executeMutation(
      { email: user.email, password, userData, userRoleRequestsByName },
      userContext(),
    );
  };

  return [state, registerAndLogin];
}

export function useLogin(): () => Promise<void> {
  const { loginWithPopup } = useAuth0();
  return () => loginWithPopup();
}

export function useLogout(): () => void {
  const { logout } = useAuth0();
  const { setToken } = useToken();
  const { setUserInfo } = useGetUserInfo();
  const { reset } = useCartContext();
  return () => {
    setToken(undefined);
    setUserInfo(undefined);
    reset();
    logout({
      returnTo: window.location.origin,
    });
  };
}

export function useToken(): {
  token: string | undefined;
  setToken: (token?: string) => void;
} {
  const [token, setToken] = useState<string | undefined>(
    getUserAuthTokenFromStorage(),
  );
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const isExpired = tokenIsExpired(token);

  useEffect(() => {
    if (isAuthenticated) {
      if ((!token || isExpired) && !isLoading) {
        getAccessTokenSilently()
          .then(value => {
            setUserAuthTokenStorage(value);
            if (value !== token) {
              setToken(value);
            }
          })
          .catch(error => {
            console.log(error);
          });
      }
    }
  }, [token, isAuthenticated, isExpired]);

  const setTokenWrapper = (newToken?: string) => {
    setToken(newToken);
    setUserAuthTokenStorage(newToken);
  };

  return { token, setToken: setTokenWrapper };
}

function userInfoToUserData(user: UserInfo): UserData_Set_Input {
  return {
    profileId: user.id,
    personFirstName: user.firstName,
    personLastName: user.lastName,
    personPrimaryEmail: user.email,
    personPrimaryMobile: user.mobile,
    personPrimaryAddress1: user.address.address1,
    personPrimaryAddress2: user.address.address2,
    personPrimaryCity: user.address.city,
    personPrimaryProvinceState: user.address.stateRegion.code,
    personPrimaryPostalCode: user.address.postalCode,
    personPrimaryCountry: user.address.country.code,
  };
}

function userDataToUserInfo(userData: UserData): UserInfo {
  return {
    id: userData.profileId,
    firstName: userData.personFirstName,
    lastName: userData.personLastName,
    email: userData.personPrimaryEmail,
    mobile: userData.personPrimaryMobile,
    address: {
      firstName: userData.personFirstName,
      lastName: userData.personLastName,
      address1: userData.personPrimaryAddress1,
      address2: userData.personPrimaryAddress2 as string | undefined,
      city: userData.personPrimaryCity,
      stateRegion: { name: '', code: userData.personPrimaryProvinceState },
      postalCode: userData.personPrimaryPostalCode,
      country: { name: '', iso3: '', code: userData.personPrimaryCountry },
      mobile: userData.personPrimaryMobile,
    },
  };
}

function combineUserInfos(a: UserInfo, b: UserInfo): UserInfo {
  return {
    ...a,
    firstName: a.firstName || b.firstName || '',
    lastName: a.lastName || b.lastName || '',
    mobile: a.mobile || b.mobile || '',
    address: {
      firstName: a.firstName || b.firstName || '',
      lastName: a.lastName || b.lastName || '',
      address1: a.address.address1 || b.address.address1 || '',
      address2: a.address.address2 || b.address.address2,
      city: a.address.city || b.address.city || '',
      postalCode: a.address.postalCode || b.address.postalCode || '',
      stateRegion: a.address.stateRegion || b.address.stateRegion || '',
      country: a.address.country || b.address.country || '',
      mobile: a.mobile || b.mobile || '',
    },
  };
}

function userContext(token?: string): Partial<OperationContext> {
  const context: Partial<OperationContext> = { url: GROUPING_SERVICE_URL };
  if (token) {
    context.fetchOptions = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }
  return context;
}

function userIdFromToken(token: string) {
  const payload = jwt_decode<any>(token);
  return payload['https://hasura.io/jwt/claims']['x-hasura-user-id'];
}

function tokenIsExpired(token: string | undefined): boolean {
  if (!token) return true;
  const payload = jwt_decode<any>(token);
  return payload.exp < Date.now() / 1000;
}

function getUserAuthTokenFromStorage(): string | undefined {
  const token = localStorage.getItem('userAuthToken');
  if (token) {
    if (tokenIsExpired(token)) {
      setUserAuthTokenStorage();
    } else {
      return token;
    }
  }
  return undefined;
}

function setUserAuthTokenStorage(token?: string) {
  if (token) {
    localStorage.setItem('userAuthToken', token);
  } else {
    localStorage.removeItem('userAuthToken');
  }
}

function getUserInfoFromStorage(): UserInfo | undefined {
  const user = localStorage.getItem('user');
  if (user) {
    return JSON.parse(user);
  }
  return undefined;
}

function setUserInfoStorage(userInfo?: UserInfo) {
  if (userInfo) {
    localStorage.setItem('user', JSON.stringify(userInfo));
  } else {
    localStorage.removeItem('user');
  }
}

export interface UseGetUserOrdersState {
  orders?: OrderType[];
  error?: Error;
  fetching: boolean;
}

export function useGetUserOrders(): UseGetUserOrdersState {
  const [orders, setOrders] = useState<OrderType[] | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);

  const client = useClient();
  const { token } = useToken();

  useEffect(() => {
    if (!token) {
      return;
    }
    setFetching(true);
    client
      .query(MagentoOrdersDocument, {}, userContext(token))
      .toPromise()
      .then(result => {
        if (result.data?.magentoOrders) {
          setOrders(result.data.magentoOrders.orders);
        }
        setFetching(false);
      })
      .catch(queryError => {
        setError(queryError);
        setFetching(false);
      });
  }, [token]);

  return { orders, error, fetching };
}

export interface UseGetUserDigitalOrderState {
  digitalProduct?: DigitalProduct;
  error?: Error;
  fetching: boolean;
}

export function useGetUserDigitalOrder(
  orderNumber: string,
  productSku: string,
): UseGetUserDigitalOrderState {
  const [digitalOrder, setDigitalOrder] = useState<any | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);

  const client = useClient();
  const { token } = useToken();

  useEffect(() => {
    if (!token) {
      return;
    }
    setFetching(true);
    client
      .query(
        GetDigitalProductsForCustomerOrderDocument,
        { orderNumber, productSku },
        userContext(token),
      )
      .toPromise()
      .then(result => {
        if (result.data?.getDigitalProductForCustomerOrder) {
          setDigitalOrder(result.data.getDigitalProductForCustomerOrder);
        }
        setFetching(false);
      })
      .catch(queryError => {
        setError(queryError);
        setFetching(false);
      });
  }, [token]);

  return { digitalProduct: digitalOrder, error, fetching };
}

export interface UseValidateAddressState {
  validatedAddress?: AddressValidationOutput;
  error?: string;
  fetching: boolean;
}

export function useValidateAddress(
  address: Address | undefined,
): UseValidateAddressState {
  const [validatedAddress, setValidatedAddress] = useState<any | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);

  const client = useClient();
  const { token } = useToken();

  useEffect(() => {
    if (!address) {
      return;
    }
    const addressInput: AddressValidationInput = {
      city: address.city,
      state: address.stateRegion.code,
      address1: address.address1,
      address2: address.address2,
      zip: address.postalCode,
    };

    setFetching(true);
    setValidatedAddress(undefined);
    client
      .query(
        ValidateAddressDocument,
        { address: addressInput },
        userContext(token),
      )
      .toPromise()
      .then(result => {
        if (result.data.validateAddress.error) {
          setError(result.data.validateAddress.error);
        } else {
          setError(undefined);
        }
        setValidatedAddress(result.data.validateAddress);
        setFetching(false);
      })
      .catch(queryError => {
        setError(queryError);
        setFetching(false);
      });
  }, [address]);

  return { fetching, validatedAddress, error };
}

export function useHubspotBlog(tagId: string) {
  const [blogPosts, setBlogPosts] = useState<any | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);
  const client = useClient();
  const { token } = useToken();
  useEffect(() => {
    setFetching(true);
    client
      .query(HubSpotBlogDocument, { tagId }, userContext(token))
      .toPromise()
      .then(result => result)
      .then(result => {
        if (result.data.hubspotBlog.error) {
          setError(result.data.hubspotBlog.error);
        } else {
          setError(undefined);
        }
        setBlogPosts(result.data.hubspotBlog);
        setFetching(false);
      })
      .catch(queryError => {
        setError(queryError);
        setFetching(false);
      });
  }, []);
  return { blogPosts, fetching, error };
}
